import React, { useState, useEffect } from "react";
import {
  X,
  Receipt,
  Download,
  MapPin,
  Building,
  CreditCard,
  DollarSign,
  ChevronRight,
  FileText,
} from "lucide-react";
import BossModalShowInvoice from "./BossModalShowInvoice";
import { useTranslation } from "react-i18next";

export default function BossStatisticByDateModalShowInvoices({
  visible,
  onClose,
}) {
  const { t } = useTranslation();
  const [allData, setAllData] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDetailModal, setShowInvoiceDetailModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Load from localStorage
  useEffect(() => {
    const data = localStorage.getItem("allData");
    if (data) setAllData(JSON.parse(data));
  }, [visible]);

  if (!visible || !allData) return null;

  const allInvoices = allData.myInvoices || [];

  const totalAmount = allInvoices.reduce(
    (sum, inv) => sum + parseFloat(inv.totalAmount || 0),
    0,
  );

  const getPlaceName = (placeId) => {
    if (!placeId) return "General";
    return (
      (allData.myPlaces || []).find((p) => p.id === placeId)?.place ||
      "Unknown Place"
    );
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const generateAllInvoicesHTML = () => {
    const invoicesHTML = allInvoices
      .map(
        (invoice) => `
      <div class="invoice-page">
        <div class="invoice-header">
          <h2>Invoice: ${invoice.inoviceNumber}</h2>
          <div class="invoice-date">Date: ${invoice.inoviceDate}</div>
          <div class="invoice-place">Place: ${getPlaceName(invoice.placeId)}</div>
        </div>
        <div class="vendor-section">
          <h3>Vendor Information</h3>
          <table class="info-table">
            <tr><td class="label">Vendor Name:</td><td class="value">${invoice.vendorName}</td></tr>
            <tr><td class="label">Business Number:</td><td class="value">${invoice.businessNumber}</td></tr>
            <tr><td class="label">Business Type:</td><td class="value">${invoice.businessType}</td></tr>
          </table>
        </div>
        <div class="financial-section">
          <h3>Financial Breakdown</h3>
          <table class="financial-table">
            <tr><td class="label">Subtotal:</td><td class="value">₪${parseFloat(invoice.subTotal).toFixed(2)}</td></tr>
            <tr><td class="label">Tax (${invoice.taxRate}%):</td><td class="value">₪${parseFloat(invoice.taxAmount).toFixed(2)}</td></tr>
            <tr class="total-row"><td class="label"><strong>Total Amount:</strong></td><td class="value"><strong>₪${parseFloat(invoice.totalAmount).toFixed(2)}</strong></td></tr>
          </table>
        </div>
      </div>`,
      )
      .join('<div class="page-break"></div>');

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
      body{font-family:Arial,sans-serif;padding:20px}
      .main-header{text-align:center;margin-bottom:40px;border-bottom:3px solid #3B82F6;padding-bottom:20px}
      .main-header h1{color:#3B82F6;margin:0 0 10px 0;font-size:32px}
      .summary-box{background:#F9FAFB;padding:20px;border-radius:8px;margin-bottom:30px;display:flex;justify-content:space-around;border:1px solid #E5E7EB}
      .summary-item{text-align:center}
      .summary-label{color:#6B7280;font-size:14px;margin-bottom:5px}
      .summary-value{color:#111827;font-size:24px;font-weight:bold}
      .summary-value.green{color:#10B981}
      .invoice-page{margin-bottom:40px;padding:20px;border:1px solid #E5E7EB;border-radius:8px;background:#fff}
      .invoice-header{border-bottom:2px solid #E5E7EB;padding-bottom:15px;margin-bottom:20px}
      .invoice-header h2{color:#111827;margin:0 0 5px 0;font-size:20px}
      .invoice-date,.invoice-place{color:#6B7280;font-size:14px}
      .vendor-section,.financial-section{margin-bottom:20px}
      .vendor-section h3,.financial-section h3{color:#111827;font-size:16px;margin-bottom:10px;border-bottom:1px solid #E5E7EB;padding-bottom:5px}
      .info-table,.financial-table{width:100%;border-collapse:collapse}
      .info-table td,.financial-table td{padding:8px;border-bottom:1px solid #F3F4F6}
      .label{color:#6B7280;font-size:14px;width:40%}
      .value{color:#111827;font-size:14px;font-weight:600}
      .total-row{border-top:2px solid #E5E7EB;background:#F9FAFB}
      .total-row .value{color:#10B981;font-size:16px}
      .page-break{page-break-after:always;margin:30px 0;border-top:2px dashed #D1D5DB}
      .footer{text-align:center;margin-top:50px;padding-top:20px;border-top:1px solid #E5E7EB;color:#6B7280;font-size:12px}
      @media print{.page-break{page-break-after:always}}
    </style></head><body>
    <div class="main-header"><h1>All Invoices</h1></div>
    <div class="summary-box">
      <div class="summary-item"><div class="summary-label">Total Invoices</div><div class="summary-value">${allInvoices.length}</div></div>
      <div class="summary-item"><div class="summary-label">Total Amount</div><div class="summary-value green">₪${totalAmount.toFixed(2)}</div></div>
    </div>
    ${invoicesHTML}
    <div class="footer">Generated on ${new Date().toLocaleDateString()}</div>
    </body></html>`;
  };

  const exportToPDF = () => {
    setShowExportMenu(false);
    if (allInvoices.length === 0) {
      alert("No invoices to export");
      return;
    }
    setIsExporting(true);
    try {
      const html = generateAllInvoicesHTML();
      const win = window.open("", "_blank");
      win.document.write(html);
      win.document.close();
      setTimeout(() => {
        win.print();
        setIsExporting(false);
      }, 500);
    } catch {
      alert("Failed to export PDF");
      setIsExporting(false);
    }
  };

  const exportToCSV = () => {
    setShowExportMenu(false);
    if (allInvoices.length === 0) {
      alert("No invoices to export");
      return;
    }
    setIsExporting(true);
    try {
      const headers = [
        "Invoice Number",
        "Invoice Date",
        "Place",
        "Vendor Name",
        "Business Number",
        "Business Type",
        "Subtotal",
        "Tax Rate",
        "Tax Amount",
        "Total Amount",
      ];

      const rows = allInvoices.map((inv) => [
        inv.inoviceNumber,
        inv.inoviceDate,
        getPlaceName(inv.placeId),
        inv.vendorName,
        inv.businessNumber,
        inv.businessType,
        `₪${parseFloat(inv.subTotal).toFixed(2)}`,
        `${inv.taxRate}%`,
        `₪${parseFloat(inv.taxAmount).toFixed(2)}`,
        `₪${parseFloat(inv.totalAmount).toFixed(2)}`,
      ]);

      const summary = [
        ["All Invoices", ""],
        ["Total Invoices", allInvoices.length],
        ["Total Amount", `₪${totalAmount.toFixed(2)}`],
        [],
        headers,
        ...rows,
      ];

      const csvContent =
        "data:text/csv;charset=utf-8," +
        summary.map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");

      const link = document.createElement("a");
      link.setAttribute("href", encodeURI(csvContent));
      link.setAttribute(
        "download",
        `All_Invoices_${new Date().toISOString().split("T")[0]}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      alert("Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
        <div
          className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl flex flex-col animate-slide-up"
          style={{ height: "90vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-5 py-5 border-b border-gray-100 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center">
                <Receipt className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {t("invoices")}
                </h2>
                <p className="text-sm text-gray-500">{t("place")}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Export dropdown */}
              {allInvoices.length > 0 && (
                <div className="relative">
                  <button
                    onClick={() => setShowExportMenu((p) => !p)}
                    disabled={isExporting}
                    className="w-9 h-9 bg-green-100 border border-green-200 rounded-full flex items-center justify-center hover:bg-green-200 transition"
                  >
                    <Download className="w-4 h-4 text-green-600" />
                  </button>
                  {showExportMenu && (
                    <div className="absolute right-0 top-11 bg-white rounded-xl shadow-lg border border-gray-100 z-50 min-w-[160px] overflow-hidden">
                      <button
                        onClick={exportToPDF}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <FileText className="w-4 h-4 text-red-500" />
                        {t("export")} PDF
                      </button>
                      <div className="h-px bg-gray-100" />
                      <button
                        onClick={exportToCSV}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                      >
                        <FileText className="w-4 h-4 text-green-500" />
                        {t("export")} CSV
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Close */}
              <button
                onClick={onClose}
                className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* ── Summary card ── */}
          <div className="mx-5 mt-4 bg-gray-50 rounded-2xl border border-gray-200 p-4 flex-shrink-0">
            <div className="flex items-center">
              <div className="flex-1 flex flex-col items-center gap-1">
                <p className="text-xs text-gray-500 font-medium">
                  {t("invoices")}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {allInvoices.length}
                </p>
              </div>
              <div className="w-px h-10 bg-gray-200" />
              <div className="flex-1 flex flex-col items-center gap-1">
                <p className="text-xs text-gray-500 font-medium">
                  {t("totalAmount")}
                </p>
                <p className="text-2xl font-bold text-green-500">
                  ₪{totalAmount.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* ── Invoices list ── */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            {allInvoices.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-20">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-5">
                  <FileText className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-xl font-bold text-gray-900 mb-2 text-center">
                  {t("noInvoicesFound")}
                </p>
                <p className="text-sm text-gray-400 text-center">
                  {t("noDataThisMonth")}
                </p>
              </div>
            ) : (
              allInvoices.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setSelectedInvoice(item);
                    setShowInvoiceDetailModal(true);
                  }}
                  className="w-full text-left bg-white rounded-2xl border border-gray-200 p-4 shadow-sm hover:shadow-md transition space-y-3"
                >
                  {/* Card header */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                      <Receipt className="w-6 h-6 text-blue-500" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900">
                        {t("invoiceNumber")}: {item.inoviceNumber}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.inoviceDate}
                      </p>
                      <p className="text-xs font-semibold text-blue-500 truncate">
                        {item.vendorName}
                      </p>
                      {/* Place badge */}
                      <div className="flex items-center gap-1 mt-1 bg-purple-50 px-2 py-1 rounded-lg w-fit">
                        <MapPin className="w-3 h-3 text-purple-500" />
                        <span className="text-[10px] font-semibold text-purple-600">
                          {getPlaceName(item.placeId)}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                      <div className="flex items-center gap-1 bg-green-100 px-2.5 py-1.5 rounded-lg">
                        <DollarSign className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-sm font-bold text-green-600">
                          ₪{parseFloat(item.totalAmount).toLocaleString()}
                        </span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>

                  {/* Business info row */}
                  <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1.5">
                      <Building className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {item.businessType || "—"}
                      </span>
                    </div>
                    <span className="text-gray-300 text-xs">•</span>
                    <div className="flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {item.businessNumber || "—"}
                      </span>
                    </div>
                  </div>

                  {/* Tax info row */}
                  <div className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2">
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">{t("subTotal")}:</span>
                      <span className="text-xs font-semibold text-gray-900">
                        ₪{parseFloat(item.subTotal).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500">
                        {t("taxRate")} ({item.taxRate}%):
                      </span>
                      <span className="text-xs font-semibold text-gray-900">
                        ₪{parseFloat(item.taxAmount).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* ── Footer ── */}
          <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0">
            <button
              onClick={onClose}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3.5 rounded-xl transition"
            >
              {t("back")}
            </button>
          </div>
        </div>
      </div>

      {/* Invoice detail modal */}
      <BossModalShowInvoice
        visible={showInvoiceDetailModal}
        onClose={() => setShowInvoiceDetailModal(false)}
        invoice={selectedInvoice}
      />

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </>
  );
}
